'use client';

import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { isFilled } from '@prismicio/client';
import type { RichTextField, LinkField } from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import {
  CalculatorInputs,
  DEFAULT_INPUTS,
  INPUTS,
  InputConfig,
  calculateStandingStill,
  formatAbbrev,
  formatExact,
} from './calculator';

type CostOfStandingStillCalculatorPrimary = {
  eyebrow?: string;
  title?: RichTextField;
  subtitle?: RichTextField;
  calculate_label?: string;
  result_cta_label?: string;
  result_cta_link?: LinkField;
};

type CostOfStandingStillCalculatorSlice = {
  slice_type: string;
  variation: string;
  primary: CostOfStandingStillCalculatorPrimary;
};

// ── Number field ────────────────────────────────────────────────────────────
// Dollars render with thousands separators as the user types; percents and
// whole numbers render plain. Blank parses back to 0 so the math never breaks.

const formatForKind = (kind: InputConfig['kind'], value: number): string => {
  if (!value) {
    return '';
  }
  if (kind === 'dollar') {
    return value.toLocaleString('en-US');
  }
  return String(value);
};

const parseForKind = (kind: InputConfig['kind'], raw: string): number => {
  const digits = raw.replace(/[^0-9]/g, '');
  if (!digits) {
    return 0;
  }
  let n = parseInt(digits, 10);
  if (!Number.isFinite(n)) {
    n = 0;
  }
  if (kind === 'percent') {
    n = Math.min(100, Math.max(0, n));
  }
  return n;
};

const NumberField: FC<{
  config: InputConfig;
  value: number;
  onChange: (value: number) => void;
}> = ({ config, value, onChange }) => {
  const isDollar = config.kind === 'dollar';
  const isPercent = config.kind === 'percent';
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[14px] font-semibold leading-[1.4] text-[#041118]">
        {config.label}
      </label>
      <div className="relative">
        {isDollar && (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] text-[#041118]/50">
            $
          </span>
        )}
        <input
          type="text"
          inputMode="numeric"
          value={formatForKind(config.kind, value)}
          onChange={(e) => onChange(parseForKind(config.kind, e.target.value))}
          placeholder="0"
          aria-label={config.label}
          className={`w-full rounded-[10px] border border-[#d6d6e0] bg-white py-2.5 text-[15px] text-[#041118] outline-none transition-colors focus:border-[#4b0fea] ${
            isDollar ? 'pl-7 pr-3' : isPercent ? 'pl-3 pr-8' : 'px-3'
          }`}
        />
        {isPercent && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[15px] text-[#041118]/50">
            %
          </span>
        )}
      </div>
      <p className="text-[12px] leading-[1.5] text-[#041118]/55">
        {config.helper}
      </p>
    </div>
  );
};

const ChevronIcon: FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
    aria-hidden="true"
  >
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CAVEAT =
  'Directional estimate for board discussion, not a precision forecast. Built on mid-point public benchmarks (Gartner, McKinsey, IBM). Adjust the assumptions to match your institution.';

const CostOfStandingStillCalculator: FC<{
  slice: CostOfStandingStillCalculatorSlice;
}> = ({ slice }) => {
  const {
    eyebrow,
    title,
    subtitle,
    calculate_label,
    result_cta_label,
    result_cta_link,
  } = slice.primary;

  const [inputs, setInputs] = useState<CalculatorInputs>(DEFAULT_INPUTS);
  const [hasCalculated, setHasCalculated] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  // Result is always derived from the inputs, so once it's shown it updates
  // live as the user edits — no need to click the button again.
  const result = useMemo(() => calculateStandingStill(inputs), [inputs]);

  const setField = useCallback(
    (key: keyof CalculatorInputs, value: number) =>
      setInputs((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const handleCalculate = useCallback(() => {
    setHasCalculated(true);
    // Bring the result into view once it has mounted (matters on phones).
    requestAnimationFrame(() =>
      resultRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      }),
    );
  }, []);

  const mainInputs = INPUTS.filter((i) => !i.advanced);
  const advancedInputs = INPUTS.filter((i) => i.advanced);

  const breakdown = [
    {
      label: 'Run-the-bank tax',
      value: result.runTheBankTax,
      note: 'Budget consumed keeping existing systems alive.',
    },
    {
      label: 'Tech-debt surcharge',
      value: result.techDebtSurcharge,
      note: 'The hidden tax on every new project (McKinsey 10–20%).',
    },
    {
      label: 'Delivery drag',
      value: result.deliveryDrag,
      note: 'Revenue forgone on releases that ship in 9–18 months instead of 3–4.',
    },
  ];

  const ctaLabel = result_cta_label || 'Turn this into a one-page board memo →';

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-white px-4 py-16 md:px-12 md:py-24"
    >
      <div className="mx-auto w-full max-w-[1080px]">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center">
          {eyebrow && (
            <span className="inline-block rounded-full border border-[#4b0fea] px-[18px] py-[8px] text-[12px] font-normal uppercase leading-[1.2] tracking-[0.12em] text-[#041118] md:text-[13px]">
              {eyebrow}
            </span>
          )}
          {isFilled.richText(title) && (
            <PrismicRichText
              field={title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="max-w-[820px] text-balance text-[32px] font-bold leading-[1.15] text-[#041118] md:text-[44px]">
                    {children}
                  </h2>
                ),
              }}
            />
          )}
          {isFilled.richText(subtitle) && (
            <div className="max-w-[760px]">
              <PrismicRichText
                field={subtitle}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-[15px] leading-[1.5] text-[#041118]/70 md:text-[16px]">
                      {children}
                    </p>
                  ),
                  strong: ({ children }) => (
                    <strong className="font-semibold text-[#041118]">
                      {children}
                    </strong>
                  ),
                }}
              />
            </div>
          )}
        </div>

        {/* Calculator card */}
        <div className="mx-auto mt-10 w-full max-w-[920px] rounded-[20px] border border-[#e6e6ed] bg-[#fbfbfd] p-6 md:p-8">
          {/* Main inputs — stack to one column on phones */}
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
            {mainInputs.map((config) => (
              <NumberField
                key={config.key}
                config={config}
                value={inputs[config.key]}
                onChange={(v) => setField(config.key, v)}
              />
            ))}
          </div>

          {/* Advanced assumptions — expand above the action row. */}
          {advancedOpen && (
            <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-6 border-t border-[#e6e6ed] pt-5 md:grid-cols-2">
              {advancedInputs.map((config) => (
                <NumberField
                  key={config.key}
                  config={config}
                  value={inputs[config.key]}
                  onChange={(v) => setField(config.key, v)}
                />
              ))}
            </div>
          )}

          {/* Action row: toggle on the left, calculate on the right. */}
          <div className="mt-6 flex flex-col gap-4 border-t border-[#e6e6ed] pt-5 sm:flex-row sm:items-center sm:justify-between">
            <button
              type="button"
              onClick={() => setAdvancedOpen((o) => !o)}
              aria-expanded={advancedOpen}
              className="flex items-center gap-2 text-[14px] font-semibold text-[#4b0fea] transition-opacity hover:opacity-80"
            >
              <ChevronIcon open={advancedOpen} />
              Adjust the benchmark assumptions
            </button>
            <button
              type="button"
              onClick={handleCalculate}
              className="inline-flex w-full items-center justify-center rounded-full bg-[#4b0fea] px-7 py-3.5 text-[16px] font-bold leading-[1.5] text-white transition-opacity hover:opacity-90 sm:w-auto"
            >
              {calculate_label || 'Calculate my number'}
            </button>
          </div>
        </div>

        {/* Result panel */}
        {hasCalculated && (
          <div
            ref={resultRef}
            className="mx-auto mt-8 w-full max-w-[920px] scroll-mt-24 overflow-hidden rounded-[20px] border border-[#e6e6ed] bg-white shadow-[0_12px_40px_-16px_rgba(4,17,24,0.25)]"
          >
            {/* Headline */}
            <div className="flex flex-col items-center gap-1 border-b border-[#e6e6ed] bg-[#041118] px-6 py-10 text-center">
              <p className="text-[12px] font-semibold uppercase tracking-[0.1em] text-white/60">
                Estimated Annual Misallocation
              </p>
              <p className="text-[48px] font-bold leading-none text-[#00f557] md:text-[64px]">
                {formatAbbrev(result.totalMisallocation)}
              </p>
              <p className="text-[14px] text-white/60">
                ({formatExact(result.totalMisallocation)} per year)
              </p>
            </div>

            <div className="flex flex-col gap-6 p-6 md:p-8">
              {/* Three-line breakdown */}
              <div className="flex flex-col">
                {breakdown.map((line, i) => (
                  <div
                    key={line.label}
                    className={`flex items-start justify-between gap-4 py-3 ${
                      i < breakdown.length - 1
                        ? 'border-b border-[#e6e6ed]'
                        : ''
                    }`}
                  >
                    <div className="flex flex-col">
                      <span className="text-[15px] font-semibold text-[#041118]">
                        {line.label}
                      </span>
                      <span className="text-[12px] leading-[1.5] text-[#041118]/55">
                        {line.note}
                      </span>
                    </div>
                    <span className="shrink-0 text-[18px] font-bold text-[#041118]">
                      {formatAbbrev(line.value)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Interpretation */}
              <p className="text-[14px] leading-[1.6] text-[#041118]/80 md:text-[15px]">
                About{' '}
                <strong className="font-semibold text-[#041118]">
                  {result.pctBudgetLocked}%
                </strong>{' '}
                of your IT budget is tied up running and working around legacy
                systems — leaving roughly{' '}
                <strong className="font-semibold text-[#041118]">
                  {result.pctAvailableForNewValue}%
                </strong>{' '}
                for genuinely new value. McKinsey&rsquo;s benchmark is that only
                5–10 cents of every technology dollar ends up creating new
                business value. The delivery-drag line is shown separately
                because it lands on the revenue statement, not the IT budget.
              </p>

              {/* Caveat */}
              <p className="text-[12px] italic leading-[1.5] text-[#041118]/50">
                {CAVEAT}
              </p>

              {/* CTA */}
              {isFilled.link(result_cta_link) ? (
                <PrismicNextLink
                  field={result_cta_link}
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#00f557] px-7 py-3.5 text-[15px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90 sm:w-auto"
                >
                  {ctaLabel}
                </PrismicNextLink>
              ) : (
                <a
                  href="#"
                  className="inline-flex w-full items-center justify-center rounded-full bg-[#00f557] px-7 py-3.5 text-[15px] font-bold leading-[1.5] text-[#002726] transition-opacity hover:opacity-90 sm:w-auto"
                >
                  {ctaLabel}
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CostOfStandingStillCalculator;
